import type { HTMLAttributes } from 'react';
import { APP_NAME } from '@/lib/constants';
import { cn } from '@/lib/utils';

export type NoPanelMarkProps = HTMLAttributes<HTMLSpanElement> & {
  /** Accessible title; set empty string + aria-hidden when paired with visible wordmark */
  title?: string;
};

/** Four crop corners around a signal cursor. Null Frame brand mark. */
export function NoPanelMark({ className, title = APP_NAME, ...props }: NoPanelMarkProps) {
  const labelled = Boolean(title);
  return (
    <span
      className={cn('np-brand-mark np-crop np-crop-ink', className)}
      role={labelled ? 'img' : undefined}
      aria-label={labelled ? title : undefined}
      aria-hidden={labelled ? undefined : true}
      {...props}
    >
      <i />
    </span>
  );
}

export type NoPanelLogoProps = {
  className?: string;
  /** Show “NoPanel” wordmark beside the mark */
  showWordmark?: boolean;
  /** Mark size classes; default h-6 w-6 (sidebar) */
  markClassName?: string;
  wordmarkClassName?: string;
};

/** Mark + optional wordmark lockup for chrome (sidebar, login, empty states). */
export function NoPanelLogo({
  className,
  showWordmark = true,
  markClassName,
  wordmarkClassName,
}: NoPanelLogoProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <NoPanelMark
        className={cn('h-6 w-6', markClassName)}
        title={showWordmark ? '' : APP_NAME}
      />
      {showWordmark ? (
        <span className={cn('font-mono text-[16px] leading-none', wordmarkClassName)}>
          <span className="sr-only">{APP_NAME}</span>
          <span aria-hidden="true">
            <b className="font-semibold text-[var(--ink-1)]">no</b>
            <span className="font-normal text-[var(--ink-3)]">panel</span>
          </span>
        </span>
      ) : null}
    </div>
  );
}
